import React from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Divider,
  List,
  ListItem,
  Stack,
  alpha,
  useTheme,
  LinearProgress,
} from '@mui/material';
import { PendingActions, TimerOutlined, ArrowForwardIos } from '@mui/icons-material';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import moment from 'moment';

export default function ConsultantActivity({ resultList = [], resultLoading }) {
  const theme = useTheme();

  // Current Period Logic
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.floor((new Date().getMonth() + 3) / 3);

  // UPDATED FILTER:
  // 1. Status is DRAFT
  // 2. Year is Current Year
  // 3. Quarter is Current Quarter
  // 4. reportType is NOT QAQC
  const currentDrafts = resultList.filter(
    (r) =>
      r.reportStatus === 'DRAFT' &&
      r.reportYear === currentYear &&
      r.reportQuarter === currentQuarter &&
      r.reportType !== 'QAQC'
  );

  if (resultLoading) {
    return (
      <Card sx={{ p: 3, borderRadius: '24px' }}>
        <LoaderSkeleton />
        <Divider sx={{ my: 2 }} />
        <LoaderSkeleton />
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '24px',
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        bgcolor: '#ffffff',
        boxShadow: '0px 12px 30px rgba(0, 0, 0, 0.04)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* HEADER SECTION */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <Box
          sx={{
            p: 1,
            borderRadius: '10px',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            display: 'flex',
          }}
        >
          <PendingActions fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            Current Drafts
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Q{currentQuarter} {currentYear} · Active Tasks
          </Typography>
        </Box>
      </Stack>

      <List disablePadding sx={{ flexGrow: 1 }}>
        {currentDrafts.length > 0 ? (
          currentDrafts.map((report) => (
            <ListItem
              key={report.reportId}
              disableGutters
              sx={{
                mb: 2,
                p: 2.5,
                borderRadius: '16px',
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.15),
                flexDirection: 'column',
                alignItems: 'stretch',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box sx={{ maxWidth: '65%' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                    {report.reportTitle}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                    <TimerOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Due: {moment(report.dueDate).format('MMM DD, YYYY')}
                    </Typography>
                  </Stack>
                </Box>

                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  endIcon={<ArrowForwardIos sx={{ fontSize: '10px !important' }} />}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    px: 2,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                >
                  Continue
                </Button>
              </Stack>

              {/* PROGRESS SECTION */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    Progress
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="primary.main">
                    {report.resultProgressPercentage}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={report.resultProgressPercentage}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            </ListItem>
          ))
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 2,
              bgcolor: alpha(theme.palette.action.disabledBackground, 0.05),
              borderRadius: '16px',
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              No pending drafts for Q{currentQuarter}, {currentYear}.
            </Typography>
            <Typography variant="caption" color="text.disabled">
              All operational reports for this period are either submitted or QAQC-restricted.
            </Typography>
          </Box>
        )}
      </List>

      {/* FOOTER */}
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Button
          fullWidth
          variant="text"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: 'text.secondary',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
          }}
        >
          View Full Report History
        </Button>
      </Box>
    </Card>
  );
}
